import React, { useState, useEffect } from 'react';
import {
  Container,
  Stack,
  Typography,
  Button,
  Card,
  Grid,
  Avatar,
  Divider,
  Fade,
  Zoom,
  Tooltip,
  Paper,
  Box,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Popover,
  Slider
} from '@mui/material';
import {
  Email as EmailIcon,
  Send as SendIcon,
  History as HistoryIcon,
  Preview as PreviewIcon,
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  FormatUnderlined as FormatUnderlinedIcon,
  FormatListBulleted as FormatListBulletedIcon,
  FormatListNumbered as FormatListNumberedIcon,
  Image as ImageIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  FormatAlignLeft as FormatAlignLeftIcon,
  FormatAlignCenter as FormatAlignCenterIcon,
  FormatAlignRight as FormatAlignRightIcon,
  FormatColorText as FormatColorTextIcon,
  FormatSize as FormatSizeIcon,
  InsertLink as InsertLinkIcon,
  EmojiEmotions as EmojiEmotionsIcon,
  TableChart as TableChartIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { GetAll, Post, apiURL } from '../../../utils/apiCalls';

// Email Templates
const EMAIL_TEMPLATES = {
  welcome: {
    name: 'Welcome Email',
    subject: 'Welcome to Perfect Layout - New Opportunities Available!',
    content: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #1976d2;">Welcome to Perfect Layout!</h2>
        <p>Dear Agent,</p>
        <p>We're excited to have you on board! New project opportunities are now available in your area.</p>
        <ul>
          <li>Exclusive project listings</li>
          <li>Competitive commission rates</li>
          <li>24/7 support</li>
        </ul>
        <p>Log in to your dashboard to view the latest offers.</p>
        <p>Best regards,<br><strong>Perfect Layout Team</strong></p>
      </div>
    `
  },
  promotion: {
    name: 'Promotion Email',
    subject: 'Special Promotion - Limited Time Offer!',
    content: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #1976d2;">Special Promotion Alert!</h2>
        <p>Dear Agent,</p>
        <p>Don't miss out on our limited-time promotion!</p>
        <div style="background-color: #f0f8ff; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #1976d2;">
          <h3 style="color: #1976d2; margin: 0;">🎉 20% Bonus Commission</h3>
          <p style="margin: 10px 0 0 0;">For all projects completed this month</p>
        </div>
        <p>This offer expires soon. Act now!</p>
        <p>Best regards,<br><strong>Perfect Layout Team</strong></p>
      </div>
    `
  },
  update: {
    name: 'System Update',
    subject: 'Important System Update - Action Required',
    content: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #1976d2;">System Update Notification</h2>
        <p>Dear Agent,</p>
        <p>We're rolling out important updates to improve your experience:</p>
        <ul>
          <li>Enhanced dashboard interface</li>
          <li>Improved project matching</li>
          <li>New reporting features</li>
        </ul>
        <p>Please log in to access the latest features.</p>
        <p>Best regards,<br><strong>Perfect Layout Team</strong></p>
      </div>
    `
  },
  newsletter: {
    name: 'Newsletter',
    subject: 'Perfect Layout Newsletter - Latest Updates & Opportunities',
    content: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="background: linear-gradient(135deg, #1976d2, #42a5f5); color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0;">
          <h1 style="margin: 0; font-size: 28px;">📧 Perfect Layout Newsletter</h1>
          <p style="margin: 10px 0 0 0; opacity: 0.9;">Your weekly update on opportunities</p>
        </div>
        <div style="padding: 20px; background: white; border: 1px solid #e0e0e0; border-top: none;">
          <h2 style="color: #1976d2; border-bottom: 2px solid #1976d2; padding-bottom: 10px;">This Week's Highlights</h2>
          <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; margin: 15px 0;">
            <h3 style="color: #1976d2; margin-top: 0;">🎉 New Projects Available</h3>
            <p>We've added <strong>25+ new projects</strong> in your area this week!</p>
          </div>
          <h3 style="color: #1976d2;">📊 Your Performance</h3>
          <ul>
            <li>Projects completed this month: <strong>12</strong></li>
            <li>Commission earned: <strong>$2,450</strong></li>
            <li>Customer rating: <strong>4.8/5 ⭐</strong></li>
          </ul>
          <div style="text-align: center; margin: 20px 0;">
            <a href="#" style="background: #1976d2; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block;">View Dashboard</a>
          </div>
        </div>
        <div style="background: #f8f9fa; padding: 15px; text-align: center; border-radius: 0 0 8px 8px; border: 1px solid #e0e0e0; border-top: none;">
          <p style="margin: 0; color: #666; font-size: 14px;">Perfect Layout Team | <a href="#" style="color: #1976d2;">Unsubscribe</a></p>
        </div>
      </div>
    `
  },
  reminder: {
    name: 'Reminder',
    subject: 'Friendly Reminder - Complete Your Profile',
    content: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 8px; padding: 20px; margin-bottom: 20px;">
          <h2 style="color: #856404; margin-top: 0;">⏰ Friendly Reminder</h2>
          <p style="color: #856404; margin-bottom: 0;">Complete your profile to unlock more opportunities!</p>
        </div>
        <h2 style="color: #1976d2;">Hi there! 👋</h2>
        <p>We noticed your profile is <strong>85% complete</strong>. Just a few more steps to maximize your earning potential:</p>
        <div style="background: #e3f2fd; padding: 15px; border-radius: 8px; margin: 15px 0;">
          <h3 style="color: #1976d2; margin-top: 0;">📝 Still Need To Add:</h3>
          <ul style="color: #1976d2;">
            <li>Professional photo</li>
            <li>Bank account details</li>
            <li>Service areas</li>
          </ul>
        </div>
        <div style="text-align: center; margin: 20px 0;">
          <a href="#" style="background: #28a745; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block;">Complete Profile</a>
        </div>
        <p style="color: #666; font-size: 14px;">This will only take 2 minutes and could increase your matches by 40%!</p>
        <p>Best regards,<br><strong>Perfect Layout Team</strong></p>
      </div>
    `
  }
};

// Custom Email Editor Component
const EmailEditor = ({ content, onChange, onPreview, onTemplateApplied }) => {
  const [editorMode, setEditorMode] = useState('visual'); // 'visual' or 'html'
  const [colorAnchor, setColorAnchor] = useState(null);
  const [fontSizeAnchor, setFontSizeAnchor] = useState(null);
  const [selectedColor, setSelectedColor] = useState('#000000');
  const [fontSize, setFontSize] = useState(14);
  const [isUpdating, setIsUpdating] = useState(false);

  const editorRef = React.useRef(null);
  const lastSelectionRef = React.useRef(null);
  const isInitialized = React.useRef(false);

  // Initialize editor content
  React.useEffect(() => {
    if (editorRef.current && !isInitialized.current) {
      if (content) {
        editorRef.current.innerHTML = content;
      } else {
        editorRef.current.innerHTML = '<p><br></p>'; // Empty paragraph to maintain focus
      }
      isInitialized.current = true;
    }
  }, [content]);

  // Handle template application and focus
  React.useEffect(() => {
    if (onTemplateApplied && editorRef.current) {
      setTimeout(() => {
        editorRef.current.focus();
        // Place cursor at the end
        const range = document.createRange();
        if (editorRef.current.childNodes.length > 0) {
          range.selectNodeContents(editorRef.current);
          range.collapse(false);
        } else {
          range.setStart(editorRef.current, 0);
          range.collapse(true);
        }
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }, 100);
    }
  }, [onTemplateApplied]);

  // Update content without losing cursor position
  React.useEffect(() => {
    if (editorRef.current && editorMode === 'visual' && isInitialized.current) {
      const currentContent = editorRef.current.innerHTML;
      if (currentContent !== content && !isUpdating) {
        setIsUpdating(true);
        
        // Save current selection
        const selection = window.getSelection();
        let range = null;
        if (selection.rangeCount > 0) {
          range = selection.getRangeAt(0);
        }
        
        // Update content
        editorRef.current.innerHTML = content;
        
        // Restore selection if possible
        if (range) {
          try {
            // Try to restore the exact range
            selection.removeAllRanges();
            selection.addRange(range);
          } catch (e) {
            // If range is invalid, place cursor at end
            const newRange = document.createRange();
            if (editorRef.current.childNodes.length > 0) {
              newRange.selectNodeContents(editorRef.current);
              newRange.collapse(false);
            } else {
              newRange.setStart(editorRef.current, 0);
              newRange.collapse(true);
            }
            selection.removeAllRanges();
            selection.addRange(newRange);
          }
        }
        
        // Use setTimeout to ensure the update is complete
        setTimeout(() => {
          setIsUpdating(false);
        }, 0);
      }
    }
  }, [content, editorMode, isUpdating]);

  // Save cursor position
  const saveSelection = () => {
    if (window.getSelection) {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        lastSelectionRef.current = selection.getRangeAt(0);
      }
    }
  };

  // Restore cursor position
  const restoreSelection = () => {
    if (lastSelectionRef.current && window.getSelection) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(lastSelectionRef.current);
    }
  };

  const execCommand = (command, value = null) => {
    saveSelection();
    document.execCommand(command, false, value);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
    setTimeout(() => {
      restoreSelection();
    }, 0);
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const insertImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      execCommand('insertImage', url);
    }
  };

  const insertTable = () => {
    const tableHTML = `
      <table style="border-collapse: collapse; width: 100%; margin: 10px 0;">
        <tr>
          <td style="border: 1px solid #ddd; padding: 8px;">Cell 1</td>
          <td style="border: 1px solid #ddd; padding: 8px;">Cell 2</td>
        </tr>
        <tr>
          <td style="border: 1px solid #ddd; padding: 8px;">Cell 3</td>
          <td style="border: 1px solid #ddd; padding: 8px;">Cell 4</td>
        </tr>
      </table>
    `;
    execCommand('insertHTML', tableHTML);
  };

  const insertEmoji = () => {
    const emojis = ['😊', '🎉', '✅', '📧', '💼', '🏠', '💰', '📈', '⭐', '🔥'];
    const emoji = emojis[Math.floor(Math.random() * emojis.length)];
    execCommand('insertText', emoji);
  };

  const handleColorChange = (color) => {
    setSelectedColor(color);
    execCommand('foreColor', color);
    setColorAnchor(null);
  };

  const handleFontSizeChange = (size) => {
    setFontSize(size);
    execCommand('fontSize', '7'); // HTML font size
    execCommand('styleWithCSS', true);
    execCommand('fontSize', size);
    setFontSizeAnchor(null);
  };

  const formatButtons = [
    { icon: <FormatBoldIcon />, command: 'bold', tooltip: 'Bold' },
    { icon: <FormatItalicIcon />, command: 'italic', tooltip: 'Italic' },
    { icon: <FormatUnderlinedIcon />, command: 'underline', tooltip: 'Underline' },
    { icon: <FormatAlignLeftIcon />, command: 'justifyLeft', tooltip: 'Align Left' },
    { icon: <FormatAlignCenterIcon />, command: 'justifyCenter', tooltip: 'Align Center' },
    { icon: <FormatAlignRightIcon />, command: 'justifyRight', tooltip: 'Align Right' },
    { icon: <FormatListBulletedIcon />, command: 'insertUnorderedList', tooltip: 'Bullet List' },
    { icon: <FormatListNumberedIcon />, command: 'insertOrderedList', tooltip: 'Numbered List' },
  ];

  const insertButtons = [
    { icon: <InsertLinkIcon />, action: insertLink, tooltip: 'Insert Link' },
    { icon: <ImageIcon />, action: insertImage, tooltip: 'Insert Image' },
    { icon: <TableChartIcon />, action: insertTable, tooltip: 'Insert Table' },
    { icon: <EmojiEmotionsIcon />, action: insertEmoji, tooltip: 'Insert Emoji' },
  ];

  return (
    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
      {/* Toolbar */}
      <Box sx={{ 
        p: 1, 
        backgroundColor: '#f8f9fa', 
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        alignItems: 'center'
      }}>
        {/* Editor Mode Toggle */}
        <ToggleButtonGroup
          value={editorMode}
          exclusive
          onChange={(e, newMode) => newMode && setEditorMode(newMode)}
          size="small"
        >
          <ToggleButton value="visual">
            <Tooltip title="Visual Editor">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FormatBoldIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption">Visual</Typography>
              </Box>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="html">
            <Tooltip title="HTML Editor">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CodeIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption">HTML</Typography>
              </Box>
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>

        <Divider orientation="vertical" flexItem />

        {/* Format Buttons */}
        {formatButtons.map((button, index) => (
          <Tooltip key={index} title={button.tooltip}>
            <IconButton
              size="small"
              onClick={() => execCommand(button.command)}
              sx={{ 
                '&:hover': { backgroundColor: '#e3f2fd' },
                '&.active': { backgroundColor: '#1976d2', color: 'white' }
              }}
            >
              {button.icon}
            </IconButton>
          </Tooltip>
        ))}

        <Divider orientation="vertical" flexItem />

        {/* Color and Font Size */}
        <Tooltip title="Text Color">
          <IconButton
            size="small"
            onClick={(e) => setColorAnchor(e.currentTarget)}
            sx={{ '&:hover': { backgroundColor: '#e3f2fd' } }}
          >
            <FormatColorTextIcon sx={{ color: selectedColor }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Font Size">
          <IconButton
            size="small"
            onClick={(e) => setFontSizeAnchor(e.currentTarget)}
            sx={{ '&:hover': { backgroundColor: '#e3f2fd' } }}
          >
            <FormatSizeIcon />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem />

        {/* Insert Buttons */}
        {insertButtons.map((button, index) => (
          <Tooltip key={index} title={button.tooltip}>
            <IconButton
              size="small"
              onClick={button.action}
              sx={{ '&:hover': { backgroundColor: '#e3f2fd' } }}
            >
              {button.icon}
            </IconButton>
          </Tooltip>
        ))}

        <Divider orientation="vertical" flexItem />

        {/* Preview Button */}
        <Tooltip title="Preview Email">
          <IconButton
            size="small"
            onClick={onPreview}
            sx={{ 
              '&:hover': { backgroundColor: '#e3f2fd' },
              color: '#1976d2'
            }}
          >
            <PreviewIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Editor Content */}
      <Box sx={{ minHeight: 300, maxHeight: 500, overflow: 'auto' }}>
        {editorMode === 'visual' ? (
          <Box
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning={true}
            onInput={(e) => {
              if (!isUpdating && editorRef.current) {
                const newContent = e.target.innerHTML;
                if (newContent !== content) {
                  // Use requestAnimationFrame to prevent cursor jumping
                  requestAnimationFrame(() => {
                    onChange(newContent);
                  });
                }
              }
            }}
            onBlur={(e) => {
              if (editorRef.current) {
                onChange(e.target.innerHTML);
              }
            }}
            onKeyUp={(e) => {
              saveSelection();
            }}
            onMouseUp={(e) => {
              saveSelection();
            }}
            onKeyDown={(e) => {
              saveSelection();
            }}
            sx={{
              p: 2,
              minHeight: 300,
              outline: 'none',
              '&:focus': { outline: 'none' },
              '& *': { fontFamily: 'inherit' }
            }}
          />
        ) : (
          <TextField
            fullWidth
            multiline
            rows={12}
            value={content}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter HTML content..."
            variant="outlined"
            sx={{ 
              '& .MuiInputBase-input': { 
                fontFamily: 'monospace',
                fontSize: '14px'
              },
              '& .MuiOutlinedInput-root': { 
                border: 'none',
                '& fieldset': { border: 'none' }
              }
            }}
          />
        )}
      </Box>

      {/* Color Picker Popover */}
      <Popover
        open={Boolean(colorAnchor)}
        anchorEl={colorAnchor}
        onClose={() => setColorAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Text Color</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {['#000000', '#1976d2', '#d32f2f', '#388e3c', '#f57c00', '#7b1fa2', '#455a64'].map((color) => (
              <Box
                key={color}
                onClick={() => handleColorChange(color)}
                sx={{
                  width: 30,
                  height: 30,
                  backgroundColor: color,
                  borderRadius: 1,
                  cursor: 'pointer',
                  border: selectedColor === color ? '2px solid #1976d2' : '1px solid #e0e0e0',
                  '&:hover': { transform: 'scale(1.1)' }
                }}
              />
            ))}
          </Box>
        </Box>
      </Popover>

      {/* Font Size Popover */}
      <Popover
        open={Boolean(fontSizeAnchor)}
        anchorEl={fontSizeAnchor}
        onClose={() => setFontSizeAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 200 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Font Size: {fontSize}px</Typography>
          <Slider
            value={fontSize}
            onChange={(e, value) => setFontSize(value)}
            min={8}
            max={48}
            step={1}
            marks={[
              { value: 8, label: '8px' },
              { value: 14, label: '14px' },
              { value: 24, label: '24px' },
              { value: 48, label: '48px' }
            ]}
          />
          <Button 
            size="small" 
            onClick={() => handleFontSizeChange(fontSize)}
            sx={{ mt: 1 }}
          >
            Apply
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

const EmailBroadcast = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('custom');
  const [isSending, setIsSending] = useState(false);
  const [broadcastStatus, setBroadcastStatus] = useState(null);
  const [broadcastLogs, setBroadcastLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (activeTab === 1) {
      loadBroadcastLogs();
    }
  }, [activeTab]);

  const loadBroadcastLogs = async () => {
    setLogsLoading(true);
    try {
      const response = await GetAll(apiURL + '/api/v1/users/email-broadcast-logs');
      if (response && response.data.status) {
        setBroadcastLogs(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to load broadcast logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const sendEmailBroadcast = async () => {
    if (!emailSubject.trim() || !emailContent.trim()) {
      toast.error('Please fill in both subject and content');
      return;
    }

    setIsSending(true);
    try {
      const response = await Post(apiURL + '/api/v1/users/send-email-to-all', {
        subject: emailSubject,
        htmlContent: emailContent
      });

      if (response && response.data.status) {
        setBroadcastStatus(response.data.data);
        toast.success('Email broadcast started successfully!');
        setActiveTab(1); // Switch to logs tab
        loadBroadcastLogs();
      } else {
        toast.error(response?.data?.message || 'Failed to start email broadcast');
      }
    } catch (error) {
      toast.error('Failed to send email broadcast');
    } finally {
      setIsSending(false);
    }
  };

  const applyTemplate = (templateKey) => {
    if (templateKey === 'custom') {
      setEmailSubject('');
      setEmailContent('');
    } else if (EMAIL_TEMPLATES[templateKey]) {
      setEmailSubject(EMAIL_TEMPLATES[templateKey].subject);
      setEmailContent(EMAIL_TEMPLATES[templateKey].content);
    }
    setSelectedTemplate(templateKey);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon />;
      case 'processing': return <ScheduleIcon />;
      case 'failed': return <ErrorIcon />;
      default: return <ScheduleIcon />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Container sx={{ m: 0, width: '100%', maxWidth: '100%' }} maxWidth={false}>
      {/* Professional Header Section */}
      <Fade in timeout={800}>
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={3} sx={{ mb: 3 }}>
            <Avatar sx={{ 
              bgcolor: '#1976d2', 
              width: 48, 
              height: 48,
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
            }}>
              <EmailIcon sx={{ fontSize: 24, color: 'white' }} />
            </Avatar>
            <Box>
              <Typography variant="h5" component="h1" fontWeight="bold" color="text.primary">
                Email Broadcast System
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Send emails to all agents and track broadcast history
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ my: 2 }} />
        </Box>
      </Fade>

      <Zoom in timeout={1000}>
        <Card elevation={4} sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
        }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab 
                label="Compose Email" 
                icon={<EmailIcon />} 
                iconPosition="start"
                sx={{ minHeight: 60 }}
              />
              <Tab 
                label="Broadcast History" 
                icon={<HistoryIcon />} 
                iconPosition="start"
                sx={{ minHeight: 60 }}
              />
            </Tabs>
          </Box>

          <Box sx={{ p: 3 }}>
            {activeTab === 0 && (
              <Grid container spacing={3}>
                {/* Email Template Selection */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Email Template</InputLabel>
                    <Select
                      value={selectedTemplate}
                      label="Email Template"
                      onChange={(e) => applyTemplate(e.target.value)}
                    >
                      <MenuItem value="custom">Custom Email</MenuItem>
                      <MenuItem value="welcome">Welcome Email</MenuItem>
                      <MenuItem value="promotion">Promotion Email</MenuItem>
                      <MenuItem value="update">System Update</MenuItem>
                      <MenuItem value="newsletter">Newsletter</MenuItem>
                      <MenuItem value="reminder">Reminder</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Email Subject */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email Subject"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Enter email subject..."
                    variant="outlined"
                  />
                </Grid>

                {/* Email Content */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Email Content
                  </Typography>
                  <EmailEditor
                    content={emailContent}
                    onChange={setEmailContent}
                    onPreview={() => setPreviewOpen(true)}
                    onTemplateApplied={selectedTemplate}
                  />
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button
                      variant="contained"
                      startIcon={<SendIcon />}
                      onClick={sendEmailBroadcast}
                      disabled={isSending || !emailSubject.trim() || !emailContent.trim()}
                      sx={{
                        backgroundColor: '#1976d2',
                        '&:hover': { backgroundColor: '#1565c0' }
                      }}
                    >
                      {isSending ? 'Sending...' : 'Send to All Agents'}
                    </Button>
                  </Stack>
                </Grid>

                {/* Sending Progress */}
                {isSending && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Sending emails to all agents. This may take a few minutes...
                    </Alert>
                    <LinearProgress />
                  </Grid>
                )}

                {/* Broadcast Status */}
                {broadcastStatus && (
                  <Grid item xs={12}>
                    <Alert severity="success">
                      Email broadcast started successfully! Job ID: {broadcastStatus.jobId}
                    </Alert>
                  </Grid>
                )}
              </Grid>
            )}

            {activeTab === 1 && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold">
                    Broadcast History
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={loadBroadcastLogs}
                    disabled={logsLoading}
                  >
                    Refresh
                  </Button>
                </Stack>

                {logsLoading ? (
                  <LinearProgress />
                ) : broadcastLogs.length === 0 ? (
                  <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <EmailIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No broadcast history found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Start by sending your first email broadcast
                    </Typography>
                  </Paper>
                ) : (
                  <List>
                    {broadcastLogs.map((log, index) => (
                      <ListItem
                        key={log.id}
                        sx={{
                          border: '1px solid #e0e0e0',
                          borderRadius: 2,
                          mb: 2,
                          '&:hover': {
                            backgroundColor: '#f8f9fa',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }
                        }}
                      >
                        <ListItemIcon>
                          <Avatar sx={{ bgcolor: `${getStatusColor(log.status)}.main` }}>
                            {getStatusIcon(log.status)}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {log.subject}
                              </Typography>
                              <Chip
                                label={log.status}
                                color={getStatusColor(log.status)}
                                size="small"
                              />
                            </Stack>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Job ID: {log.job_id}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Total: {log.total_users} | Success: {log.success_count} | Failed: {log.failure_count}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Created: {formatDate(log.created_at)}
                              </Typography>
                            </Box>
                          }
                        />
                        <Tooltip title="View Details">
                          <IconButton onClick={() => {
                            console.log(log);
                            setEmailSubject(log.subject);
                            setEmailContent(log.html_content);
                            setPreviewOpen(true);
                          }}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}
          </Box>
        </Card>
      </Zoom>

      {/* Email Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={2}>
            <PreviewIcon />
            <Typography variant="h6">Email Preview</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Subject:
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {emailSubject}
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              p: 2,
              backgroundColor: '#fafafa',
              minHeight: 300
            }}
            dangerouslySetInnerHTML={{ __html: emailContent }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EmailBroadcast;
